import { useState } from 'react';
import type { AttributeStateResponse, SourceTypeCode } from '../../api/types';
import { CONFIDENCE_COPY, TRAJECTORY_COPY, bandFor, formatTimestamp, sourceLabel } from './attributeDisplay';
import { AttributeHistory } from './AttributeHistory';
import { AssessmentForm, canAssess } from './AssessmentForm';
import { AssessmentRecords } from './AssessmentRecords';
import styles from './AttributeRow.module.css';

/**
 * One attribute, for one person.
 *
 * This component carries most of the product's integrity, so the rules it
 * follows are worth stating:
 *
 * 1. **An unassessed attribute is never drawn as a rating.** No bar, no
 *    number, no zero — the row says "Not assessed" and stays visible so the
 *    gap in coverage is legible (docs/UX_ARCHITECTURE.md §3.3).
 * 2. **Rating and confidence are separate things, side by side.** The
 *    confidence label is never folded into the number, and a low-confidence
 *    rating is drawn less solidly so the eye registers the uncertainty
 *    before reading the word.
 * 3. **Disagreement is a visible flag**, not something the composite is
 *    allowed to absorb (docs/ASSESSMENT_FRAMEWORK.md §5.2).
 * 4. **Trajectory says "Insufficient history"** where there is one point or
 *    none, rather than drawing a direction (§7).
 * 5. **The composite is one interaction from its sources** (§5.1) — the row
 *    expands to the per-source breakdown that produced it, including the
 *    weights, because "the weights exist, are visible, and are never hidden
 *    inside opaque code" (§5.3).
 * 6. **The trajectory is one further interaction from its history**
 *    (Mission 012) — the expanded row opens the recorded points the
 *    direction was classified from. A stated direction whose basis cannot
 *    be reached is an unexplained conclusion, which
 *    docs/PRODUCT_CONSTITUTION.md rules out.
 *
 * The history is a second, nested disclosure rather than a route, a modal
 * or a always-loaded block: the profile lists 23 attributes, and fetching a
 * history for each of them on page load would be 23 requests for something
 * nobody has asked to see. Opening one row's history costs one request.
 *
 * 7. **An assessment is recorded here too** (Mission 014), for the same
 *    reason: this is where the subject and the attribute are already
 *    established, and where the composite, its sources, the confidence and
 *    the recorded history are all on screen to inform what someone types.
 *    The affordance appears only where the API says it would succeed —
 *    the viewer's derived relationship must be one the attribute permits.
 */
export function AttributeRow({
  state,
  employeeId,
  employeeName,
  viewerCanAssessAs,
  onAssessmentRecorded,
}: {
  state: AttributeStateResponse;
  employeeId: string;
  employeeName: string;
  /** The perspective the API says this viewer stands in, or null. */
  viewerCanAssessAs: SourceTypeCode | null;
  /** Re-reads the profile's authoritative state after a write. */
  onAssessmentRecorded: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [recordsOpen, setRecordsOpen] = useState(false);
  /**
   * Bumped after a write so an open history panel re-fetches.
   *
   * It is part of the panel's `key`, which remounts it — the smallest way to
   * make it re-read. The alternative, appending the new point locally, would
   * mean the browser deciding what the new composite and confidence are,
   * which Mission 012 established it must never do.
   */
  const [dataVersion, setDataVersion] = useState(0);
  const { attribute, composite, confidence, trajectory } = state;
  const assessed = composite !== null;
  const band = assessed ? bandFor(composite.composite) : null;
  const confidenceCopy = CONFIDENCE_COPY[confidence.level];
  const trajectoryCopy = TRAJECTORY_COPY[trajectory.direction];
  const historyRegionId = `history-${attribute.id}`;
  const recordsRegionId = `assessments-${attribute.id}`;
  const assessable = canAssess(attribute, viewerCanAssessAs);

  function handleRecorded() {
    // Re-read, never patch. Every figure this row shows — composite, band,
    // confidence, variance, trajectory — is derived by the domain over the
    // whole record set, so the only honest way to learn what the new
    // assessment meant is to ask again.
    onAssessmentRecorded();
    setDataVersion((version) => version + 1);
  }

  /**
   * The button, or the open form — used in both the assessed and the
   * never-assessed branches, since an attribute nobody has assessed is
   * exactly where recording one matters most.
   *
   * A plain element rather than a nested component function. Declaring a
   * component inside another component gives it a new identity on every
   * render, so React unmounts and remounts it — which here silently threw
   * away the form's state (including the confirmation just shown) the
   * moment the profile re-read after a successful write.
   */
  const assessmentAffordance = !assessable ? null : formOpen ? (
    <AssessmentForm
      employeeId={employeeId}
      employeeName={employeeName}
      attribute={attribute}
      sourceType={viewerCanAssessAs}
      onRecorded={handleRecorded}
      onClose={() => setFormOpen(false)}
    />
  ) : (
    <button type="button" className={styles.assessToggle} onClick={() => setFormOpen(true)}>
      Record an assessment
    </button>
  );

  return (
    <li className={`${styles.row} ${assessed ? '' : styles.unassessed}`}>
      <button
        type="button"
        className={assessed ? styles.summary : styles.summaryCompact}
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
      >
        <span className={styles.name} title={attribute.definition}>
          {attribute.name}
        </span>

        {assessed ? (
          <>
            <span className={styles.measure}>
              <span className={styles.value} data-confidence={confidence.level}>
                {composite.composite.toFixed(1)}
              </span>
              <span
                className={styles.track}
                data-confidence={confidence.level}
                aria-hidden="true"
                title={`${band!.label} (band ${band!.index} of 5)`}
              >
                <span
                  className={styles.fill}
                  style={{
                    width: `${(composite.composite / 20) * 100}%`,
                    background: `var(--band-${band!.index})`,
                  }}
                />
              </span>
              <span className="srOnly">
                {composite.composite.toFixed(1)} out of 20, {band!.label}
              </span>
            </span>

            <span className={styles.band}>{band!.label}</span>

            <span className={styles.confidence} data-confidence={confidence.level} title={confidenceCopy.meaning}>
              {confidenceCopy.label}
            </span>

            <span className={styles.trajectory} title={trajectoryCopy.label}>
              <span aria-hidden="true" className={styles.trajectoryGlyph} data-direction={trajectory.direction}>
                {trajectoryCopy.glyph}
              </span>
              <span className={styles.trajectoryLabel}>{trajectoryCopy.label}</span>
            </span>

            <span className={styles.flags}>
              {confidence.highVariance ? (
                <span className={styles.flag} title="Sources materially disagree about this attribute.">
                  Sources disagree
                </span>
              ) : null}
              {confidence.stale ? (
                <span className={styles.flag} title="The most recent assessment is old enough to reduce confidence.">
                  Stale
                </span>
              ) : null}
            </span>
          </>
        ) : (
          /* Confidence and trajectory are not shown here: for an attribute
             with zero assessments they are always exactly "Unconfirmed" and
             "Insufficient history" (the domain's own confidence and
             trajectory rules — see docs/DOMAIN_DECISIONS.md's confidence and
             trajectory policies), so repeating them on every unassessed row
             says nothing that "Not assessed" has not already said, at the
             cost of the wall of identical columns this compacting removes.
             The full detail panel below still states them explicitly. */
          <span className={styles.notAssessed}>Not assessed</span>
        )}
      </button>

      {expanded ? (
        <div className={styles.detail}>
          <p className={styles.definition}>{attribute.definition}</p>

          {assessed ? (
            <>
              <table className={styles.sources}>
                <caption className={styles.sourcesCaption}>
                  How this composite is made up — each source's own rating and the weight it carries
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Source</th>
                    <th scope="col">Rating</th>
                    <th scope="col">Weight</th>
                    <th scope="col">Most recent</th>
                  </tr>
                </thead>
                <tbody>
                  {composite.contributors.map((contributor) => (
                    <tr key={contributor.sourceType}>
                      <th scope="row">
                        {sourceLabel(contributor.sourceType)}
                        {contributor.recordCount > 1 ? (
                          <span className={styles.recordCount}> ({contributor.recordCount} assessments)</span>
                        ) : null}
                      </th>
                      <td className={styles.numeric}>{contributor.value}</td>
                      <td className={styles.numeric}>{contributor.weight.toFixed(2)}</td>
                      <td>{formatTimestamp(contributor.mostRecentAssessedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {composite.unavailableSourceTypes.length > 0 ? (
                <p className={styles.note}>
                  No assessment from: {composite.unavailableSourceTypes.map(sourceLabel).join(', ')}.
                </p>
              ) : null}

              <dl className={styles.facts}>
                <div>
                  <dt>Confidence</dt>
                  <dd>{confidenceCopy.meaning}</dd>
                </div>
                <div>
                  <dt>Evidence</dt>
                  <dd>
                    {confidence.evidenceCount === 0
                      ? 'No evidence items linked'
                      : `${confidence.evidenceCount} evidence ${confidence.evidenceCount === 1 ? 'item' : 'items'} linked`}
                  </dd>
                </div>
                {confidence.spread !== null ? (
                  <div>
                    <dt>Spread between sources</dt>
                    <dd>
                      {confidence.spread} {confidence.spread === 1 ? 'point' : 'points'}
                      {confidence.highVariance ? ' — flagged as material disagreement' : ''}
                    </dd>
                  </div>
                ) : null}
                <div>
                  <dt>History</dt>
                  <dd>
                    {trajectory.historyPointCount < 2
                      ? `${trajectory.historyPointCount} assessment point — not enough to establish a direction`
                      : `${trajectory.historyPointCount} assessment points · ${trajectoryCopy.label}${
                          trajectory.delta !== null
                            ? ` (${trajectory.delta > 0 ? '+' : ''}${trajectory.delta} since the previous point)`
                            : ''
                        }`}
                  </dd>
                </div>
              </dl>

              {/* The route from the stated direction to the records behind
                  it. Offered whenever the attribute has been assessed —
                  including at one point, where seeing that single point is
                  precisely what distinguishes "not enough history" from
                  "no assessment". */}
              <button
                type="button"
                className={styles.historyToggle}
                aria-expanded={historyOpen}
                aria-controls={historyRegionId}
                onClick={() => setHistoryOpen((open) => !open)}
              >
                {historyOpen ? 'Hide recorded history' : 'Show recorded history'}
                <span className={styles.historyCount}>
                  {trajectory.historyPointCount === 1
                    ? '1 recorded point'
                    : `${trajectory.historyPointCount} recorded points`}
                </span>
              </button>

              {/* The individual records the composite is made of — and the
                  only place a replaced or withdrawn one stays visible. Lazy
                  like the history beside it: 23 attributes should not each
                  fetch their records unasked. */}
              <button
                type="button"
                className={styles.historyToggle}
                aria-expanded={recordsOpen}
                aria-controls={recordsRegionId}
                onClick={() => setRecordsOpen((open) => !open)}
              >
                {recordsOpen ? 'Hide recorded assessments' : 'Show recorded assessments'}
              </button>

              {recordsOpen ? (
                <AssessmentRecords
                  key={`${recordsRegionId}-${dataVersion}`}
                  employeeId={employeeId}
                  employeeName={employeeName}
                  attribute={attribute}
                  viewerCanAssessAs={viewerCanAssessAs}
                  regionId={recordsRegionId}
                  onChanged={handleRecorded}
                />
              ) : null}

              {historyOpen ? (
                <AttributeHistory
                  key={`${historyRegionId}-${dataVersion}`}
                  employeeId={employeeId}
                  attributeDefinitionId={attribute.id}
                  attributeName={attribute.name}
                  regionId={historyRegionId}
                />
              ) : null}

            </>
          ) : (
            <>
              <p className={styles.note}>
                Nobody has assessed {attribute.name} for this person yet. This is an absence of information, not a
                low rating.
              </p>
            </>
          )}

          {/* One position, outside the assessed/unassessed branches.
              Rendered inside a branch it would be at a different place in
              the tree depending on which one applied — so recording the
              *first* assessment for an attribute switched branches, React
              remounted the form, and the confirmation the reader had just
              been given vanished. */}
          {assessmentAffordance}

          <p className={styles.scaleHint}>
            <span>1 — {attribute.lowEndMeaning}</span>
            <span>20 — {attribute.highEndMeaning}</span>
          </p>
        </div>
      ) : null}
    </li>
  );
}
